import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import type { IIndividualProduct } from '@/service/vpc/types/IIndividualProducts';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import FormatDateTime from '@/components/FormatDateTime';
import Table from '@/components/Table';
import useMediaQuery from '@/hooks/useMediaQuery';
import { loaderUnitSpecificationsPage } from '@/loaders/unit.specifications.loader';
import ProductType from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/specifications/_components/ProductType';
import {
  getIndividualProductsQueryOptions,
  getLatestOperatingHoursQueryOptions,
  unitByIdQueryOptions,
} from '@/service/vpc/queryOptions';
import { chassis, product } from '@/utils';

type SpecificationsData = {
  engine: string;
  segment: string;
  ratedPower: string;
  ratedSpeed: string;
};

export function UnitsSpecificationsPage() {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const { endCustomerUnitId } = useParams({
    strict: false,
  });

  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit);
  const { data: productsData } = useQuery(getIndividualProductsQueryOptions({ serialNumbers: serialNumbers || [] }));
  const { data: latestOperatingHours } = useQuery(
    getLatestOperatingHoursQueryOptions({
      endcustomerunitids: unit && !unit.claiming ? [unit.endCustomerUnitId] : [],
    })
  );

  const enginesAndMotors = product.getOnlyEnginesAndMotors(productsData || []);
  const products = product.getUnique(enginesAndMotors);

  const specificationsData: SpecificationsData[] = products?.map((product) => ({
    engine: product.productDesignation,
    segment: unit?.mainSegment || 'Not applicable',
    ratedPower: `${product.ratedPower} hp`,
    ratedSpeed: `${product.ratedSpeed} rpm`,
  }));

  const columns = [
    {
      accessorKey: 'driveLines',
      id: 'driveLines',
      header: t(($) => $.components.table.specifikationer_table.driveline),
      sortingFn: (rowA, rowB) => {
        const driveLineA = chassis.getDrivelinePosition(
          unit!,
          chassis.getId({
            chassisSeries: rowA.original.driveLineIdChassisSeries,
            chassisNumber: rowA.original.driveLineIdChassisNumber,
          })
        );
        const driveLineB = chassis.getDrivelinePosition(
          unit!,
          chassis.getId({
            chassisSeries: rowB.original.driveLineIdChassisSeries,
            chassisNumber: rowB.original.driveLineIdChassisNumber,
          })
        );

        return driveLineA.position - driveLineB.position;
      },
      cell: (info) => {
        const row = info.row.original;
        const driveLineLabel = chassis.getDrivelinePosition(
          unit!,
          chassis.getId({
            chassisSeries: row.driveLineIdChassisSeries,
            chassisNumber: row.driveLineIdChassisNumber,
          })
        );
        return (
          <div className="flex flex-col">
            <Typography variant="caption1" className="text-subtle">
              {driveLineLabel.positionName}
            </Typography>
            <Typography variant="caption2" className="text-subtle">
              Position: {driveLineLabel.position === 0 ? 'Single' : driveLineLabel.position}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.productCategory,
      id: 'productCategory',
      header: t(($) => $.components.table.specifikationer_table.product_type),
      cell: (info) => {
        return <ProductType type={info.getValue<string>()} />;
      },
    },
    {
      accessorFn: (row) => row.productDesignation,
      id: 'productDesignation',
      header: t(($) => $.components.table.specifikationer_table.product),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.serialNumber,
      id: 'serialNumber',
      header: t(($) => $.components.table.specifikationer_table.serial_number),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'operatingHours',
      id: 'operatingHours',
      header: t(($) => $.components.table.specifikationer_table.operating_hours),
      cell: (info) => {
        const chassisSeries = info.row.original.driveLineIdChassisSeries;
        const chassisNumber = info.row.original.driveLineIdChassisNumber;
        const latestKnownOperationHours = latestOperatingHours?.find(
          (data) => data.ChassisId === `${chassisSeries}${chassisNumber}`
        );

        return latestKnownOperationHours ? (
          <div className="flex flex-col">
            <Typography variant="caption1" className="text-subtle">
              {Math.round(latestKnownOperationHours.Value).toLocaleString()} h
            </Typography>
            <FormatDateTime
              date={latestKnownOperationHours?.Date}
              variant="caption1"
              type="datetime"
              className="text-subtle"
            />
          </div>
        ) : (
          <></>
        );
      },
    },
  ] as ColumnDef<IIndividualProduct>[];

  const columnsSpecifications = [
    {
      accessorFn: (row) => row.engine,
      id: 'engine',
      header: t(($) => $.components.table.specifikationer_table.engine),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.segment,
      id: 'segment',
      header: t(($) => $.components.table.specifikationer_table.segment),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.ratedPower,
      id: 'ratedPower',
      header: t(($) => $.components.table.specifikationer_table.rated_power),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.ratedSpeed,
      id: 'ratedSpeed',
      header: t(($) => $.components.table.specifikationer_table.rated_speed),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
  ] as ColumnDef<SpecificationsData>[];

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  return (
    <div className="flex flex-col gap-9">
      <Table
        key={`products-${mqSize}`}
        isLoading={false}
        data={productsData || []}
        defaultSorting={[
          {
            id: 'driveLines',
            desc: false,
          },
        ]}
        columns={columns}
        showHeader={false}
        showSearch={false}
        showPagination={false}
      />
      <Table
        key={`specifications-${mqSize}`}
        isLoading={false}
        data={specificationsData || []}
        columns={columnsSpecifications}
        headerTitle={t(($) => $.pages.unit.specifications.title)}
        showSearch={false}
        showPagination={false}
      />
    </div>
  );
}

export const Route = createFileRoute(
  '/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/specifications/'
)({
  component: UnitsSpecificationsPage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitSpecificationsPage,
});
